import {
  Button,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  lighten
} from "@mui/material";
import { FiCopy } from "react-icons/fi";
import { useTranslation } from "react-i18next";
import { useState } from "react";

interface UATSnapshotButtonProps {
  disabled: boolean;
  onCreateSnapshot: () => void;
  isInProgress: boolean;
  isLoading: boolean;
  taskUuid: string;
}

export const UATSnapshotButton = ({
  disabled,
  onCreateSnapshot,
  isInProgress,
  isLoading,
  taskUuid,
}: UATSnapshotButtonProps) => {
  const [dialogIsOpen, setDialogIsOpen] = useState(false);
  const { t } = useTranslation();

  const getDisabledReason = () => {
    if (isLoading) {
      return t("general.loading");
    }
    if (isInProgress) {
      return t("taskRunner.copyTenantData.button.inProgressDescription");
    }
    return "";
  };

  const openDialog = () => {
    setDialogIsOpen(true);
  }

  const closeDialog = () => {
    setDialogIsOpen(false);
  }

  const handleCreateSnapshot = () => {
    onCreateSnapshot();
    setDialogIsOpen(false);
  }

  const button = (
    <Button
      variant="outlined"
      color="primary"
      disabled={disabled}
      loading={isInProgress || isLoading}
      loadingPosition="start"
      onClick={openDialog}
      startIcon={<FiCopy />}
      disableElevation
      disableRipple
      data-testid={`uat-snapshot-button-${taskUuid}`}
      sx={{
        color: (theme) => theme.palette.brand.main,
        transition: "transform 150ms ease, background-color 150ms cubic-bezier(.4,0,.2,1)",
        "&:hover": {
          backgroundColor: (theme) => lighten(theme.palette.brand.main, 0.9),
        },
        border: (theme) => `1px solid ${theme.palette.brand.main}`,
        "&:active": {
          transform: "scale(0.99)",
        },
        "&:focus-visible": {
          boxShadow: (theme) => `0 0 0 3px ${theme.palette.primary.main}40`,
        },
      }}
    >
      {isLoading
        ? t("general.loading")
        : isInProgress
          ? t(`taskRunner.copyTenantData.button.inProgress`)
          : t(`taskRunner.copyTenantData.button.start`)
      }
    </Button>
  );

  if (disabled && (isInProgress || isLoading)) {
    return (
      <Tooltip title={getDisabledReason()} arrow>
        <span>{button}</span>
      </Tooltip>
    );
  }

  return (
    <>
      {button}
      <Dialog
        open={dialogIsOpen}
        onClose={closeDialog}
        aria-labelledby="snapshot-dialog-title"
      >
        <DialogTitle id="snapshot-dialog-title">
          {t("taskRunner.copyTenantData.dialog.title")}
        </DialogTitle>
        <DialogContent>
          {t("taskRunner.copyTenantData.dialog.description")}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog}>
            {t("general.cancel")}
          </Button>
          <Button
            startIcon={<FiCopy />}
            onClick={handleCreateSnapshot}
            color="brand"
            disableElevation
            disableRipple
            variant="contained"
            disabled={disabled}
          >
            {t("taskRunner.copyTenantData.dialog.confirm")}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
